/**
 * System
 *
 * The software provider's own-organization hub — the self-serve counterpart
 * to the super admin's Software Providers pages:
 *
 *   - Organization  — name + contact details (PATCH /companies/:id allowed fields)
 *   - Billing       — plan summary (price/device, deployed count) and payment
 *                     methods (Stripe card on file; friendly state when Stripe
 *                     isn't configured on the environment)
 *   - Users         — the existing Users page (its own Users/Audit tabs)
 *
 * Company admins only, pinned to their own company. Super admins manage any
 * company from /software-providers instead; the nav hides System from them.
 *
 * Tab selection is URL-driven (`?tab=`) like the Integrations hub.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styled from '@emotion/styled';
import { IconX } from '@tabler/icons-react';
import { useAuth } from '../context/AuthContext';
import { isSuperAdmin, isCompanyAdmin } from '../services/users';
import { getCompany, updateCompany } from '../services/companies';
import { getAlertRules, updateAlertRule } from '../services/alerts';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  SectionTitle,
  DetailCard,
  FormGroup,
  Label,
  Input,
  Button,
  LinkButton,
  HelperText,
  EmptyState,
  MutedText,
  ErrorAlert,
  StatusChip,
  ModalOverlay,
  ModalCard,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  H2Title,
} from '../components/styled';
import { SegmentedTabs } from '../components/SegmentedTabs';
import Users from './Users';

// Billing & payment moved to the Purchases page (Structure.md): System is
// company management (org profile, alerts) + user management.
type SystemTab = 'organization' | 'alerts' | 'users';

const TABS: { id: SystemTab; label: string }[] = [
  { id: 'organization', label: 'Organization' },
  { id: 'alerts',       label: 'Alerts' },
  { id: 'users',        label: 'Users' },
];

function isSystemTab(v: string | null): v is SystemTab {
  return v === 'organization' || v === 'alerts' || v === 'users';
}

const FormCard = styled(DetailCard)`
  max-width: 560px;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing(4)};

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const FullWidth = styled.div`
  grid-column: 1 / -1;
`;

const SaveRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-top: ${({ theme }) => theme.spacing(5)};
`;

const SavedNote = styled.span`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.accent.positive ?? theme.accent.primary};
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${({ theme }) => theme.spacing(4)};
  margin-bottom: ${({ theme }) => theme.spacing(6)};
`;

const SummaryCell = styled(DetailCard)`
  padding: ${({ theme }) => theme.spacing(4)};
`;

const SummaryLabel = styled.div`
  font-size: ${({ theme }) => theme.font.size.xs};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.font.color.secondary};
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

const SummaryValue = styled.div`
  font-size: ${({ theme }) => theme.font.size.xl};
  font-weight: ${({ theme }) => theme.font.weight.semibold};
`;

const CardRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(3, 0)};
  border-bottom: 1px solid ${({ theme }) => theme.border.primary};

  &:last-of-type {
    border-bottom: none;
  }
`;

const CardBrand = styled.span`
  font-weight: ${({ theme }) => theme.font.weight.medium};
  text-transform: capitalize;
`;

const CardActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
`;

// Mount point for the Stripe Payment Element.
const StripeMount = styled.div`
  min-height: 120px;
`;

// ─── Stripe.js loader ────────────────────────────────────────────────────────

let stripeJsPromise: Promise<any> | null = null;

/** Load https://js.stripe.com/v3 once and return the Stripe constructor. */
function loadStripeJs(): Promise<any> {
  if ((window as any).Stripe) return Promise.resolve((window as any).Stripe);
  if (!stripeJsPromise) {
    stripeJsPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/v3/';
      script.async = true;
      script.onload = () => {
        if ((window as any).Stripe) resolve((window as any).Stripe);
        else reject(new Error('Stripe.js loaded but window.Stripe is missing'));
      };
      script.onerror = () => {
        stripeJsPromise = null;
        reject(new Error('Failed to load Stripe.js'));
      };
      document.head.appendChild(script);
    });
  }
  return stripeJsPromise;
}

// ─── Add-card modal ──────────────────────────────────────────────────────────

function AddCardModal({ companyId, onClose, onSaved }: {
  companyId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [error, setError] = useState('');
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const mountRef = useRef<HTMLDivElement>(null);
  const stripeRef = useRef<any>(null);
  const elementsRef = useRef<any>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const intent = await createCardSetupIntent(companyId);
        if (!intent.publishableKey) {
          throw new Error('Stripe publishable key is not configured (STRIPE_PUBLISHABLE_KEY)');
        }
        const Stripe = await loadStripeJs();
        if (cancelled) return;
        const stripe = Stripe(intent.publishableKey);
        const elements = stripe.elements({ clientSecret: intent.clientSecret });
        const paymentElement = elements.create('payment');
        paymentElement.mount(mountRef.current!);
        paymentElement.on('ready', () => { if (!cancelled) setReady(true); });
        stripeRef.current = stripe;
        elementsRef.current = elements;
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.response?.data?.message || err?.response?.data?.error || err.message || 'Failed to start card setup');
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  async function handleSave() {
    if (!stripeRef.current || !elementsRef.current) return;
    setSaving(true);
    setError('');
    try {
      const result = await stripeRef.current.confirmSetup({
        elements: elementsRef.current,
        redirect: 'if_required',
      });
      if (result.error) {
        setError(result.error.message || 'Card could not be saved');
      } else {
        onSaved();
      }
    } catch (err: any) {
      setError(err.message || 'Card could not be saved');
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalOverlay onClick={onClose}>
      <ModalCard size="md" onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <H2Title>Add payment method</H2Title>
          <ModalCloseButton onClick={onClose} aria-label="Close"><IconX size={18} stroke={2} /></ModalCloseButton>
        </ModalHeader>
        <ModalBody>
          {error && <ErrorAlert>{error}</ErrorAlert>}
          <StripeMount ref={mountRef} />
          {!ready && !error && <MutedText>Loading secure card form…</MutedText>}
          <HelperText>
            Card details go directly to Stripe — they never touch Simkura servers.
          </HelperText>
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="button" onClick={handleSave} disabled={!ready || saving}>
            {saving ? 'Saving…' : 'Save card'}
          </Button>
        </ModalFooter>
      </ModalCard>
    </ModalOverlay>
  );
}

// ─── Organization tab ────────────────────────────────────────────────────────

function OrganizationPanel({ companyId }: { companyId: string }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ name: '', contact_email: '', contact_phone: '', address: '' });
  const [saved, setSaved] = useState(false);

  const { data: company, isLoading } = useQuery({
    queryKey: ['system-company', companyId],
    queryFn: () => getCompany(companyId as any),
  });

  useEffect(() => {
    if (company) {
      setForm({
        name: company.name || '',
        contact_email: (company as any).contact_email || '',
        contact_phone: (company as any).contact_phone || '',
        address: (company as any).address || '',
      });
    }
  }, [company]);

  const saveMutation = useMutation({
    mutationFn: () => {
      const updates: Record<string, string> = {};
      if (form.name.trim()) updates.name = form.name.trim();
      updates.contact_email = form.contact_email.trim();
      updates.contact_phone = form.contact_phone.trim();
      updates.address = form.address.trim();
      return updateCompany(companyId as any, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-company', companyId] } as any);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
  });

  if (isLoading) return <EmptyState><p>Loading organization…</p></EmptyState>;

  return (
    <FormCard>
      <SectionTitle>Organization profile</SectionTitle>
      {saveMutation.isError && (
        <ErrorAlert>
          {(saveMutation.error as any)?.response?.data?.error || 'Failed to save changes'}
        </ErrorAlert>
      )}
      <form onSubmit={(e) => { e.preventDefault(); saveMutation.mutate(); }}>
        <FieldGrid>
          <FullWidth>
            <FormGroup>
              <Label>Organization name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </FormGroup>
          </FullWidth>
          <FormGroup>
            <Label>Contact email</Label>
            <Input
              type="email"
              value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              placeholder="billing@yourcompany.com"
            />
          </FormGroup>
          <FormGroup>
            <Label>Contact phone</Label>
            <Input
              value={form.contact_phone}
              onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
            />
          </FormGroup>
          <FullWidth>
            <FormGroup>
              <Label>Address</Label>
              <Input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
            </FormGroup>
          </FullWidth>
        </FieldGrid>
        <SaveRow>
          <Button type="submit" disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Saving…' : 'Save changes'}
          </Button>
          {saved && <SavedNote>Saved.</SavedNote>}
        </SaveRow>
      </form>
    </FormCard>
  );
}

// ─── Billing & payment tab ───────────────────────────────────────────────────

function BillingPanel({ companyId }: { companyId: string }) {
  const queryClient = useQueryClient();
  const [showAddCard, setShowAddCard] = useState(false);

  const { data: summary } = useQuery({
    queryKey: ['system-billing-summary', companyId],
    queryFn: () => getBillingSummary(companyId as any) as Promise<any>,
  });

  const { data: pm, isLoading: pmLoading } = useQuery({
    queryKey: ['system-payment-methods', companyId],
    queryFn: () => getPaymentMethods(companyId),
  });

  const refreshMethods = () =>
    queryClient.invalidateQueries({ queryKey: ['system-payment-methods', companyId] } as any);

  const defaultMutation = useMutation({
    mutationFn: (paymentMethodId: string) => setDefaultPaymentMethod(companyId, paymentMethodId),
    onSuccess: refreshMethods,
  });

  const removeMutation = useMutation({
    mutationFn: (paymentMethodId: string) => removePaymentMethod(companyId, paymentMethodId),
    onSuccess: refreshMethods,
  });

  const { data: invoiceData } = useQuery({
    queryKey: ['system-invoices', companyId],
    queryFn: () => getInvoices(companyId),
  });

  const { data: statementData } = useQuery({
    queryKey: ['system-service-statements', companyId],
    queryFn: () => getServiceStatements(companyId),
  });
  const statements = statementData?.statements ?? [];
  const statementVariant = (s: string) => (s === 'paid' ? 'green' : s === 'invoiced' ? 'warning' : 'gray');

  const pricePerDevice = Number(summary?.billing?.pricePerDevice ?? 0);
  const deployed = Number(summary?.devices?.deployed ?? 0);
  const currency = (summary?.billing?.currency || 'usd').toUpperCase();
  const monthlyTotal = pricePerDevice * deployed;

  const methods: SavedPaymentMethod[] = pm?.methods || [];
  const money = (minor: number, cur: string) => `${(minor / 100).toFixed(2)} ${(cur || 'usd').toUpperCase()}`;
  const invoiceVariant = (status: string) =>
    status === 'paid' ? 'green' : status === 'open' ? 'warning' : 'gray';

  return (
    <>
      {pm?.apiAccess?.gated && (
        <ErrorAlert as="div">
          <strong>API access is inactive.</strong> Your API keys return
          {' '}<code>403 payment_required</code> until a payment method is added below.
        </ErrorAlert>
      )}
      <SummaryGrid>
        <SummaryCell>
          <SummaryLabel>Billing method</SummaryLabel>
          <SummaryValue style={{ textTransform: 'capitalize' }}>{summary?.billing?.method || '—'}</SummaryValue>
        </SummaryCell>
        <SummaryCell>
          <SummaryLabel>Price per device</SummaryLabel>
          <SummaryValue>{pricePerDevice.toFixed(2)} {currency}</SummaryValue>
        </SummaryCell>
        <SummaryCell>
          <SummaryLabel>Deployed devices</SummaryLabel>
          <SummaryValue>{deployed}</SummaryValue>
        </SummaryCell>
        <SummaryCell>
          <SummaryLabel>Est. monthly total</SummaryLabel>
          <SummaryValue>{monthlyTotal.toFixed(2)} {currency}</SummaryValue>
        </SummaryCell>
      </SummaryGrid>

      <FormCard>
        <SectionTitle>Payment methods</SectionTitle>
        {(defaultMutation.isError || removeMutation.isError) && (
          <ErrorAlert>
            {((defaultMutation.error || removeMutation.error) as any)?.response?.data?.error
              || 'Payment method update failed'}
          </ErrorAlert>
        )}

        {pmLoading && <MutedText>Loading payment methods…</MutedText>}

        {pm && !pm.configured && (
          <EmptyState>
            <p>Card payments aren't enabled on this environment yet.</p>
            <MutedText>
              Once Stripe is configured, you'll add and manage your organization's
              cards here.
            </MutedText>
          </EmptyState>
        )}

        {pm?.configured && (
          <>
            {methods.length === 0 ? (
              <EmptyState>
                <p>No payment method on file.</p>
                <MutedText>Add a card so your subscription and hardware purchases can be billed.</MutedText>
              </EmptyState>
            ) : (
              methods.map((m) => (
                <CardRow key={m.id}>
                  <div>
                    <CardBrand>{m.brand || 'card'}</CardBrand>
                    {' '}•••• {m.last4}
                    <MutedText>
                      &nbsp;· expires {String(m.expMonth).padStart(2, '0')}/{m.expYear}
                    </MutedText>
                  </div>
                  <CardActions>
                    {pm.defaultPaymentMethodId === m.id ? (
                      <StatusChip variant="green">Default</StatusChip>
                    ) : (
                      <LinkButton
                        onClick={() => defaultMutation.mutate(m.id)}
                        disabled={defaultMutation.isPending}
                      >
                        Make default
                      </LinkButton>
                    )}
                    <LinkButton
                      onClick={() => {
                        if (window.confirm(`Remove ${m.brand || 'card'} ending ${m.last4}?`)) {
                          removeMutation.mutate(m.id);
                        }
                      }}
                      disabled={removeMutation.isPending}
                    >
                      Remove
                    </LinkButton>
                  </CardActions>
                </CardRow>
              ))
            )}
            <SaveRow>
              <Button type="button" variant="secondary" onClick={() => setShowAddCard(true)}>
                Add card
              </Button>
            </SaveRow>
          </>
        )}
      </FormCard>

      <FormCard style={{ marginTop: 24 }}>
        <SectionTitle>Invoices</SectionTitle>
        {invoiceData && !invoiceData.configured && (
          <MutedText>Invoice history appears here once payments are enabled.</MutedText>
        )}
        {invoiceData?.configured && invoiceData.invoices.length === 0 && (
          <EmptyState><p>No invoices yet.</p></EmptyState>
        )}
        {invoiceData?.upcoming && (
          <HelperText>
            Upcoming: {money(invoiceData.upcoming.amountDue, invoiceData.upcoming.currency)}
            {invoiceData.upcoming.periodEnd && ` · bills ${new Date(invoiceData.upcoming.periodEnd).toLocaleDateString()}`}
          </HelperText>
        )}
        {(invoiceData?.invoices || []).map((inv) => (
          <CardRow key={inv.id}>
            <div>
              {inv.created ? new Date(inv.created).toLocaleDateString() : '—'}
              {inv.number && <MutedText>&nbsp;· {inv.number}</MutedText>}
              <MutedText>&nbsp;· {money(inv.total, inv.currency)}</MutedText>
            </div>
            <CardActions>
              <StatusChip variant={invoiceVariant(inv.status)}>{inv.status}</StatusChip>
              {inv.hostedInvoiceUrl && (
                <a href={inv.hostedInvoiceUrl} target="_blank" rel="noreferrer">View</a>
              )}
            </CardActions>
          </CardRow>
        ))}
      </FormCard>

      <FormCard style={{ marginTop: 24 }}>
        <SectionTitle>Service statements</SectionTitle>
        <HelperText>
          Monthly service billing for your deployed marketplace devices — each
          device at the rate from its order.
        </HelperText>
        {statements.length === 0 ? (
          <EmptyState><p>No service statements yet.</p></EmptyState>
        ) : (
          statements.map((s) => (
            <CardRow key={s.id}>
              <div>
                {new Date(s.period_start).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })}
                <MutedText>
                  &nbsp;· {s.lines.reduce((n, l) => n + l.device_count, 0)} device{s.lines.reduce((n, l) => n + l.device_count, 0) === 1 ? '' : 's'}
                  &nbsp;· {money(s.total_cents, s.currency)}
                </MutedText>
                {s.status === 'invoiced' && s.charge_failure_reason && (
                  <div><MutedText>unpaid — {s.charge_failure_reason.replace(/_/g, ' ')}</MutedText></div>
                )}
              </div>
              <CardActions>
                <StatusChip variant={statementVariant(s.status)}>{s.status}</StatusChip>
              </CardActions>
            </CardRow>
          ))
        )}
      </FormCard>

      {showAddCard && (
        <AddCardModal
          companyId={companyId}
          onClose={() => setShowAddCard(false)}
          onSaved={() => { setShowAddCard(false); refreshMethods(); }}
        />
      )}
    </>
  );
}

// ─── Alerts tab ──────────────────────────────────────────────────────────────

function AlertsPanel({ companyId }: { companyId: string }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ enabled: true, threshold_minutes: '5', cooldown_minutes: '60' });
  const [saved, setSaved] = useState(false);

  const { data: rulesData, isLoading } = useQuery({
    queryKey: ['system-alert-rules', companyId],
    queryFn: getAlertRules,
  });
  const rule = rulesData?.rules?.[0] ?? null;
  const noRuleYet = !isLoading && !rule;

  useEffect(() => {
    if (rule) {
      setForm({
        enabled: rule.enabled,
        threshold_minutes: String(rule.threshold_minutes ?? 5),
        cooldown_minutes: String(rule.cooldown_minutes ?? 60),
      });
    }
  }, [rule]);

  const saveMutation = useMutation({
    mutationFn: () => updateAlertRule(companyId, {
      enabled: form.enabled,
      threshold_minutes: Math.max(1, parseInt(form.threshold_minutes, 10) || 5),
      cooldown_minutes: Math.max(1, parseInt(form.cooldown_minutes, 10) || 60),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-alert-rules', companyId] } as any);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
  });

  return (
    <FormCard>
      <SectionTitle>Gateway offline alerts</SectionTitle>
      <HelperText>
        When your gateway stops reporting for longer than the threshold, an
        alert fires and every company admin is emailed. Repeat notifications
        wait out the cooldown. Alerts show on the Dashboard, where they can be
        acknowledged and resolved.
      </HelperText>

      {noRuleYet && (
        <ErrorAlert as="div">
          No alert rule is configured yet — offline checks are <strong>skipped</strong> for
          your organization until you save one.
        </ErrorAlert>
      )}
      {saveMutation.isError && (
        <ErrorAlert>
          {(saveMutation.error as any)?.response?.data?.error || 'Failed to save alert rule'}
        </ErrorAlert>
      )}

      <form onSubmit={(e) => { e.preventDefault(); saveMutation.mutate(); }}>
        <FieldGrid>
          <FullWidth>
            <FormGroup>
              <Label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                />
                Alerting enabled
              </Label>
            </FormGroup>
          </FullWidth>
          <FormGroup>
            <Label>Offline threshold (minutes)</Label>
            <Input
              type="number"
              min={1}
              value={form.threshold_minutes}
              onChange={(e) => setForm({ ...form, threshold_minutes: e.target.value })}
              required
            />
            <HelperText>How long the gateway must be silent before alerting.</HelperText>
          </FormGroup>
          <FormGroup>
            <Label>Cooldown (minutes)</Label>
            <Input
              type="number"
              min={1}
              value={form.cooldown_minutes}
              onChange={(e) => setForm({ ...form, cooldown_minutes: e.target.value })}
              required
            />
            <HelperText>Minimum wait before the same alert emails again.</HelperText>
          </FormGroup>
        </FieldGrid>
        <SaveRow>
          <Button type="submit" disabled={saveMutation.isPending || isLoading}>
            {saveMutation.isPending ? 'Saving…' : noRuleYet ? 'Enable alerting' : 'Save changes'}
          </Button>
          {saved && <SavedNote>Saved.</SavedNote>}
        </SaveRow>
      </form>
    </FormCard>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

function System() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get('tab');
  const activeTab: SystemTab = isSystemTab(tabParam) ? tabParam : 'organization';
  const setTab = (id: SystemTab) => setSearchParams(id === 'organization' ? {} : { tab: id });

  const superAdmin = isSuperAdmin(user);
  const companyAdmin = isCompanyAdmin(user);
  const companyId = user?.company_id ? String(user.company_id) : '';

  if (superAdmin) {
    return (
      <PageContainer>
        <PageHeader><PageTitle>System</PageTitle></PageHeader>
        <EmptyState>
          <p>System is the company admin's self-serve hub.</p>
          <MutedText>As a super admin, manage any organization from Software Providers.</MutedText>
        </EmptyState>
      </PageContainer>
    );
  }

  if (!companyAdmin || !companyId) {
    return (
      <PageContainer>
        <PageHeader><PageTitle>System</PageTitle></PageHeader>
        <EmptyState>
          <p>Only company admins can manage organization settings.</p>
          <MutedText>Contact your administrator for access.</MutedText>
        </EmptyState>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Users renders its own page header — only show ours on the other tabs. */}
      {activeTab !== 'users' && (
        <PageHeader>
          <PageTitle>System</PageTitle>
        </PageHeader>
      )}

      {activeTab === 'users' ? (
        <>
          <SegmentedTabs tabs={TABS} activeId={activeTab} onChange={(id) => setTab(id as SystemTab)} />
          <Users />
        </>
      ) : (
        <>
          <SegmentedTabs tabs={TABS} activeId={activeTab} onChange={(id) => setTab(id as SystemTab)} />
          {activeTab === 'organization' && <OrganizationPanel companyId={companyId} />}
          {activeTab === 'alerts' && <AlertsPanel companyId={companyId} />}
        </>
      )}
    </PageContainer>
  );
}

export default System;
